/**
 * 统一错误对象与「可操作的中文文案」映射。
 *
 * 原则（评审硬要求：不允许静默失败）：
 * - 所有请求失败都变成 ApiError，带 code / status / details，界面必须显示。
 * - 文案必须具体、可操作：说清楚「哪一步失败、为什么、下一步怎么办」，
 *   禁止只显示「出错了」。
 * 本文件零依赖（不 import client / store），避免循环引用。
 */

export type ApiErrorCode =
  | 'VALIDATION_FAILED'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'RATE_LIMITED'
  | 'INTERNAL'
  | 'NETWORK'
  | 'BAD_RESPONSE';

const CODE_FALLBACK_MESSAGES: Record<string, string> = {
  VALIDATION_FAILED: '提交的内容不符合要求，请检查表单中标红的字段',
  UNAUTHORIZED: '登录状态已过期，请重新登录后继续',
  FORBIDDEN: '当前账号没有权限执行该操作（如需权限请联系赛事管理员）',
  NOT_FOUND: '要操作的数据不存在，可能已被删除，请刷新后重试',
  CONFLICT: '数据冲突：该记录已存在或状态已变更，请刷新后重试',
  RATE_LIMITED: '操作过于频繁，已被限流，请等待约 1 分钟后再试',
  INTERNAL: '服务器内部错误，请稍后重试；若持续出现请把错误码告诉管理员',
  NETWORK: '无法连接后端服务：请确认 API 已启动（默认 http://localhost:3000），并检查 .env 的 VITE_API_BASE_URL',
  BAD_RESPONSE: '后端返回了不符合约定的数据格式（缺少 ok 字段），请检查前后端版本是否一致',
};

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: unknown;

  constructor(code: string, message: string, status = 0, details: unknown = null) {
    super(message.length > 0 ? message : CODE_FALLBACK_MESSAGES[code] ?? '请求失败');
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }

  get fallbackMessage(): string {
    return CODE_FALLBACK_MESSAGES[this.code] ?? '请求失败，请稍后重试';
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function errorCodeOf(error: unknown): string | null {
  return isApiError(error) ? error.code : null;
}

export interface FieldIssue {
  field: string;
  message: string;
}

/** 后端 VALIDATION_FAILED 的 details 兼容解析（[{field,message}] 或 {field: message}） */
export function validationIssues(error: unknown): FieldIssue[] {
  if (!isApiError(error)) return [];
  const details = error.details;
  const out: FieldIssue[] = [];
  if (Array.isArray(details)) {
    for (const item of details) {
      if (typeof item !== 'object' || item === null) continue;
      const record = item as Record<string, unknown>;
      const message = record.message;
      if (typeof message !== 'string' || message.length === 0) continue;
      const field = record.field;
      out.push({ field: typeof field === 'string' ? field : '', message });
    }
    return out;
  }
  if (typeof details === 'object' && details !== null) {
    for (const [field, value] of Object.entries(details as Record<string, unknown>)) {
      if (typeof value === 'string' && value.length > 0) out.push({ field, message: value });
    }
  }
  return out;
}

/** 字段级错误（表单内联展示用） */
export function fieldIssueOf(error: unknown, field: string): string | null {
  const hit = validationIssues(error).find((issue) => issue.field === field);
  return hit ? hit.message : null;
}

/**
 * 给用户看的一句话：优先用后端 message，其次用校验明细，最后退到错误码文案。
 * 调用方应带上「动作」前缀，例如 `保存画像失败：${toUserMessage(err)}`。
 */
export function toUserMessage(error: unknown): string {
  if (isApiError(error)) {
    if (error.code === 'VALIDATION_FAILED') {
      const issues = validationIssues(error);
      const message = error.message.trim();
      if (issues.length > 0) {
        const details = issues.map((issue) => issue.message).join('；');
        // 合并完整 message（含格式示例）与 details 明细，避免只剩「格式不正确」这类短句
        if (message.length > 0 && message !== 'Error' && !message.includes(details)) {
          return `${message}：${details}`;
        }
        return details;
      }
    }
    const message = error.message.trim();
    if (message.length > 0 && message !== 'Error') return message;
    return error.fallbackMessage;
  }
  if (error instanceof Error) {
    return error.message.trim().length > 0 ? error.message : '发生未知错误，请刷新页面重试';
  }
  return '发生未知错误，请刷新页面重试';
}

/** 排查用的补充信息（错误码 / HTTP 状态） */
export function errorMetaOf(error: unknown): string | null {
  if (!isApiError(error)) return null;
  const parts: string[] = [`错误码 ${error.code}`];
  if (error.status > 0) parts.push(`HTTP ${error.status}`);
  return parts.join(' · ');
}